import { Component, Input, OnInit } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, of } from 'rxjs';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../services/auth.service';
import {
  BotLinkRequest,
  LinkableTutor,
  LinkedTutorSummary,
  TutorService,
} from '../../services/tutor.service';

/**
 * Linked-tutors panel rendered inside the Twin (alpha_dozent) AI-settings.
 *
 * Two modes, decided by `AuthService.isClientWide()`:
 *  - Admin / manager: manage the tutor↔twin links directly (attach /
 *    detach via bot_dozent_link) — mirrors the tutor-side dozent-link
 *    picker so the Twin becomes a full management surface.
 *  - Rights-limited dozent (role='user' + DOZENT feature set): cannot
 *    reach the client's tutors, so they submit a link REQUEST that an
 *    admin/manager approves. Pending/decided requests are shown inline.
 *
 * All dialogs are Nebular-based (ConfirmDialogComponent) — no browser
 * confirm/alert.
 */
@Component({
  selector: 'app-twin-linked-tutors',
  templateUrl: './twin-linked-tutors.component.html',
  styleUrl: './twin-linked-tutors.component.scss',
})
export class TwinLinkedTutorsComponent implements OnInit {
  @Input() dozentBotId!: string;

  isClientWide = false;
  loading = false;

  linkedTutors: LinkedTutorSummary[] = [];
  linkableTutors: LinkableTutor[] = [];
  myRequests: BotLinkRequest[] = [];

  /** Admin direct-attach panel. */
  attachOpen = false;
  attachTargetId = '';

  /** Rights-limited-user request panel. */
  requestOpen = false;
  requestTargetId = '';
  requestNote = '';

  constructor(
    public tutorService: TutorService,
    private auth: AuthService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isClientWide = this.auth.isClientWide();
    this.refresh();
  }

  /** Targets not already linked to THIS twin. */
  get availableTargets(): LinkableTutor[] {
    return this.linkableTutors.filter((t) => !t.already_linked);
  }

  /** Requests worth showing to the requester: pending (awaiting decision)
   *  and rejected (with the reason). Approved ones are dropped — the tutor
   *  now appears in `linkedTutors`, so a separate "GENEHMIGT" row would just
   *  duplicate it and clutter the clean post-link state. */
  get visibleRequests(): BotLinkRequest[] {
    return this.myRequests.filter(
      (r) => r.status === 'pending' || r.status === 'rejected'
    );
  }

  /** Requestable = available AND no open request for it yet. */
  get requestableTargets(): LinkableTutor[] {
    const pending = new Set(
      this.myRequests
        .filter((r) => r.status === 'pending')
        .map((r) => r.target_tutor_bot_id)
    );
    return this.availableTargets.filter((t) => !pending.has(t.id));
  }

  private refresh(): void {
    if (!this.dozentBotId) return;
    this.loading = true;
    // Each call degrades to [] on its own error so one failing endpoint
    // (e.g. a 403 on requests) never blanks the whole panel — the linkable
    // list must still render so a rights-limited dozent can request.
    forkJoin({
      linked: this.tutorService
        .listLinkedTutors(this.dozentBotId)
        .pipe(catchError(() => of([]))),
      linkable: this.tutorService
        .listLinkableTutors(this.dozentBotId)
        .pipe(catchError(() => of([]))),
      requests: this.tutorService
        .listLinkRequests()
        .pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ linked, linkable, requests }) => {
        this.linkedTutors = linked;
        this.linkableTutors = linkable;
        this.myRequests = requests.filter(
          (r) => r.dozent_bot_id === this.dozentBotId
        );
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  templateLabelKey(templateName?: string | null): string {
    return `tutor.twinLinks.tpl.${templateName || 'unknown'}`;
  }

  statusKey(status: string): string {
    return `tutor.twinLinks.status.${status}`;
  }

  // ---- Admin: direct attach / detach --------------------------------

  toggleAttach(): void {
    this.attachOpen = !this.attachOpen;
    this.attachTargetId = '';
  }

  confirmAttach(): void {
    const target = this.availableTargets.find(
      (t) => t.id === this.attachTargetId
    );
    if (!target) return;
    if (target.linked_to_other_twin) {
      this.dialogService
        .open(ConfirmDialogComponent, {
          context: {
            title: this.translate.instant('tutor.twinLinks.swapConfirm.title'),
            message: this.translate.instant(
              'tutor.twinLinks.swapConfirm.message',
              { tutor: target.name }
            ),
          },
        })
        .onClose.subscribe((ok: boolean) => {
          if (ok) this.doAttach(target.id);
        });
    } else {
      this.doAttach(target.id);
    }
  }

  private doAttach(tutorId: string): void {
    this.loading = true;
    this.tutorService.attachDozent(tutorId, this.dozentBotId).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant('tutor.twinLinks.attached'),
          ''
        );
        this.attachOpen = false;
        this.attachTargetId = '';
        this.refresh();
      },
      error: () => {
        this.loading = false;
        this.toastr.danger(
          this.translate.instant('tutor.twinLinks.attachFailed'),
          ''
        );
      },
    });
  }

  detach(tutor: LinkedTutorSummary): void {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: this.translate.instant('tutor.twinLinks.detachConfirm.title'),
          message: this.translate.instant(
            'tutor.twinLinks.detachConfirm.message',
            { tutor: tutor.name }
          ),
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.loading = true;
        this.tutorService.detachDozent(tutor.id).subscribe({
          next: () => {
            this.toastr.success(
              this.translate.instant('tutor.twinLinks.detached'),
              ''
            );
            this.refresh();
          },
          error: () => {
            this.loading = false;
            this.toastr.danger(
              this.translate.instant('tutor.twinLinks.detachFailed'),
              ''
            );
          },
        });
      });
  }

  // ---- Rights-limited user: request a link --------------------------

  toggleRequest(): void {
    this.requestOpen = !this.requestOpen;
    this.requestTargetId = '';
    this.requestNote = '';
  }

  submitRequest(): void {
    if (!this.requestTargetId) return;
    this.loading = true;
    this.tutorService
      .createLinkRequest(this.dozentBotId, {
        target_tutor_bot_id: this.requestTargetId,
        requester_note: this.requestNote?.trim() || null,
      })
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant('tutor.twinLinks.requestSent'),
            ''
          );
          this.requestOpen = false;
          this.requestTargetId = '';
          this.requestNote = '';
          this.refresh();
        },
        error: (err) => {
          this.loading = false;
          const code = err?.error?.detail;
          const key =
            code === 'already_linked'
              ? 'tutor.twinLinks.errAlreadyLinked'
              : code === 'request_already_pending'
                ? 'tutor.twinLinks.errPending'
                : 'tutor.twinLinks.requestFailed';
          this.toastr.danger(this.translate.instant(key), '');
        },
      });
  }
}
